import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functionsUS } from '../services/firebase';
import {
  CountryMaster,
  RegionMaster,
  AreaMaster,
  DistrictMaster,
  CategoryMaster,
  SubcategoryMaster,
  DetailedTypeMaster,
} from '../types/master';

interface MasterDataSelectorProps {
  // Location selection
  countryCode?: string;
  regionId?: string;
  areaId?: string;
  districtId?: string;
  onLocationChange: (location: { countryCode: string; regionId: string; areaId: string; districtId?: string } | undefined) => void;

  // Business type selection
  categories?: string[];
  subcategories?: string[];
  detailedTypes?: string[];
  onBusinessTypesChange: (businessTypes: { categories: string[]; subcategories: string[]; detailedTypes: string[] } | undefined) => void;

  // Optional
  disabled?: boolean;
}

const MasterDataSelector: React.FC<MasterDataSelectorProps> = ({
  countryCode,
  regionId,
  areaId,
  districtId,
  onLocationChange,
  categories = [],
  subcategories = [],
  detailedTypes = [],
  onBusinessTypesChange,
  disabled = false,
}) => {
  // Location master data
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [regions, setRegions] = useState<RegionMaster[]>([]);
  const [areas, setAreas] = useState<AreaMaster[]>([]);
  const [districts, setDistricts] = useState<DistrictMaster[]>([]);

  // Business type master data
  const [categoryMasters, setCategoryMasters] = useState<CategoryMaster[]>([]);
  const [subcategoryMasters, setSubcategoryMasters] = useState<SubcategoryMaster[]>([]);
  const [detailedTypeMasters, setDetailedTypeMasters] = useState<DetailedTypeMaster[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
    loadCategories();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (countryCode) {
      loadRegions(countryCode);
    } else {
      setRegions([]);
      setAreas([]);
    }
  }, [countryCode]);

  // Load areas when region changes
  useEffect(() => {
    if (countryCode && regionId) {
      loadAreas(countryCode, regionId);
    } else {
      setAreas([]);
      setDistricts([]);
    }
  }, [countryCode, regionId]);

  // Load districts when area changes
  useEffect(() => {
    if (countryCode && regionId && areaId) {
      loadDistricts(countryCode, regionId, areaId);
    } else {
      setDistricts([]);
    }
  }, [countryCode, regionId, areaId]);

  // Load subcategories when categories change
  useEffect(() => {
    if (categories.length > 0) {
      loadSubcategories(categories);
    } else {
      setSubcategoryMasters([]);
      setDetailedTypeMasters([]);
    }
  }, [categories.join(',')]);

  // Load detailed types based on selected categories
  useEffect(() => {
    if (categories.length > 0) {
      loadDetailedTypes(categories);
    } else {
      setDetailedTypeMasters([]);
    }
  }, [categories.join(',')]);

  const loadCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'countries', includeInactive: false });
      const data = result.data as { data: any[] };
      setCountries(data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      })));
    } catch (err: any) {
      console.error('Error loading countries:', err);
      setError('国データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async (countryCodeParam: string) => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'regions', includeInactive: false });
      const data = result.data as { data: any[] };
      const allRegions = data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      }));
      setRegions(allRegions.filter((r: RegionMaster) => r.countryCode === countryCodeParam));
    } catch (err: any) {
      console.error('Error loading regions:', err);
      setError('地方データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadAreas = async (countryCodeParam: string, regionIdParam: string) => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'areas', includeInactive: false });
      const data = result.data as { data: any[] };
      const allAreas = data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      }));
      setAreas(allAreas.filter((a: AreaMaster) =>
        a.countryCode === countryCodeParam && a.regionId === regionIdParam
      ));
    } catch (err: any) {
      console.error('Error loading areas:', err);
      setError('エリアデータの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadDistricts = async (countryCodeParam: string, regionIdParam: string, areaIdParam: string) => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'districts', includeInactive: false });
      const data = result.data as { data: any[] };
      const allDistricts = data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      }));
      setDistricts(allDistricts.filter((d: DistrictMaster) =>
        d.countryCode === countryCodeParam && d.regionId === regionIdParam && d.areaId === areaIdParam
      ));
    } catch (err: any) {
      console.error('Error loading districts:', err);
      setError('地区データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'categories', includeInactive: false });
      const data = result.data as { data: any[] };
      setCategoryMasters(data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      })));
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError('業種データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryIds: string[]) => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'subcategories', includeInactive: false });
      const data = result.data as { data: any[] };
      const allSubcategories = data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      }));
      setSubcategoryMasters(allSubcategories.filter((s: SubcategoryMaster) =>
        categoryIds.includes(s.categoryId)
      ));
    } catch (err: any) {
      console.error('Error loading subcategories:', err);
      setError('業種詳細データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedTypes = async (categoryIds: string[]) => {
    try {
      setLoading(true);
      setError(null);
      const getMasterData = httpsCallable(functionsUS, 'getMasterData');
      const result = await getMasterData({ type: 'detailedTypes', includeInactive: false });
      const data = result.data as { data: any[] };
      const allDetailedTypes = data.data.map((item: any) => ({
        ...item,
        createdAt: item.createdAt?.toDate?.() || new Date(item.createdAt),
        updatedAt: item.updatedAt?.toDate?.() || new Date(item.updatedAt),
      }));
      setDetailedTypeMasters(allDetailedTypes.filter((d: DetailedTypeMaster) =>
        d.applicableCategories.some(cat => categoryIds.includes(cat))
      ));
    } catch (err: any) {
      console.error('Error loading detailed types:', err);
      setError('店舗タイプデータの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (value: string) => {
    onLocationChange(value ? { countryCode: value, regionId: '', areaId: '', districtId: undefined } : undefined);
  };

  const handleRegionChange = (value: string) => {
    if (countryCode) {
      onLocationChange(value ? { countryCode, regionId: value, areaId: '', districtId: undefined } : { countryCode, regionId: '', areaId: '', districtId: undefined });
    }
  };

  const handleAreaChange = (value: string) => {
    if (countryCode && regionId) {
      onLocationChange(value ? { countryCode, regionId, areaId: value, districtId: undefined } : { countryCode, regionId, areaId: '', districtId: undefined });
    }
  };

  const handleDistrictChange = (value: string) => {
    if (countryCode && regionId && areaId) {
      onLocationChange({ countryCode, regionId, areaId, districtId: value || undefined });
    }
  };

  const handleCategoryChange = (value: string) => {
    const newCategories = value ? [value] : [];
    onBusinessTypesChange({
      categories: newCategories,
      subcategories: [],
      detailedTypes: [],
    });
  };

  const handleSubcategoryChange = (value: string) => {
    const newSubcategories = value ? [value] : [];
    onBusinessTypesChange({
      categories,
      subcategories: newSubcategories,
      detailedTypes,
    });
  };

  const handleDetailedTypeChange = (value: string) => {
    const newDetailedTypes = value ? [value] : [];
    onBusinessTypesChange({
      categories,
      subcategories,
      detailedTypes: newDetailedTypes,
    });
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        店舗所在地（マスタデータ）
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            label="国"
            value={countryCode || ''}
            onChange={(e) => handleCountryChange(e.target.value)}
            disabled={disabled || loading}
            required
          >
            <MenuItem value="">選択してください</MenuItem>
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.code}>
                {country.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            label="地方"
            value={regionId || ''}
            onChange={(e) => handleRegionChange(e.target.value)}
            disabled={disabled || loading || !countryCode}
            required
          >
            <MenuItem value="">選択してください</MenuItem>
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                {region.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            label="エリア（都道府県/県）"
            value={areaId || ''}
            onChange={(e) => handleAreaChange(e.target.value)}
            disabled={disabled || loading || !regionId}
            required
          >
            <MenuItem value="">選択してください</MenuItem>
            {areas.map((area) => (
              <MenuItem key={area.id} value={area.id}>
                {area.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            label="地区（市区町村）"
            value={districtId || ''}
            onChange={(e) => handleDistrictChange(e.target.value)}
            disabled={disabled || loading || !areaId}
          >
            <MenuItem value="">選択してください</MenuItem>
            {districts.map((district) => (
              <MenuItem key={district.id} value={district.id}>
                {district.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        業種分類（マスタデータ）
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="業種（大分類）"
            value={categories[0] || ''}
            onChange={(e) => handleCategoryChange(e.target.value)}
            disabled={disabled || loading}
            required
          >
            <MenuItem value="">選択してください</MenuItem>
            {categoryMasters.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.icon} {category.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="業種詳細"
            value={subcategories[0] || ''}
            onChange={(e) => handleSubcategoryChange(e.target.value)}
            disabled={disabled || loading || categories.length === 0}
          >
            <MenuItem value="">選択してください</MenuItem>
            {subcategoryMasters.map((subcategory) => (
              <MenuItem key={subcategory.id} value={subcategory.id}>
                {subcategory.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="店舗タイプ"
            value={detailedTypes[0] || ''}
            onChange={(e) => handleDetailedTypeChange(e.target.value)}
            disabled={disabled || loading || categories.length === 0}
          >
            <MenuItem value="">選択してください</MenuItem>
            {detailedTypeMasters.map((detailedType) => (
              <MenuItem key={detailedType.id} value={detailedType.id}>
                {detailedType.name.ja}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default MasterDataSelector;
